/**
 * pagesVmBuilder — traduz `GET /v1/pages` + `GET /v1/domains/status` no ViewModel do contrato @v3.
 *
 * Função PURA de propósito: é onde os quatro estados de página são decididos, e isso precisa ser
 * testável sem montar React. O `usePagesScreen` cuida de estado, efeitos e ações.
 *
 * ⚠️ Toda string que chega na View sai formatada daqui. Se a View precisar de `new Date(...)`,
 * `toLocaleString` ou de um `if` de regra de negócio, o contrato falhou.
 */
import { formatCurrency, formatDate, formatRelativeDistance } from '../../utils/formatters';

const URL_BASE = import.meta.env.VITE_PAGES_URL || 'https://pages.agendario.com.br';

/** Vocabulário do catálogo PageType (int). Ver `catalogs/pageCatalog.generated`. */
const TIPO = { 1: 'Link in Bio', 2: 'Profissional', 3: 'Empresa' };

/**
 * Os QUATRO estados, com o que cada um significa **para o visitante** — não para o sistema.
 *
 * A tela antiga tinha dois chips ("Publicada" / "Rascunho") e escondia as duas situações que
 * realmente importam: a página no ar mostrando conteúdo velho, e a que caiu do ar sem ninguém
 * perceber.
 */
const ESTADO = {
  published: {
    statusLabel: 'Publicada',
    statusTone: 'success',
    statusHint: 'No ar e igual ao que você editou.',
  },
  pending: {
    statusLabel: 'Alterações não publicadas',
    statusTone: 'warning',
    statusHint: 'Quem abrir o link vê a versão anterior.',
  },
  unpublished: {
    statusLabel: 'Fora do ar',
    statusTone: 'error',
    statusHint: 'Quem tiver o link salvo não consegue mais abrir.',
  },
  draft: {
    statusLabel: 'Rascunho',
    statusTone: 'neutral',
    statusHint: 'Ainda não foi publicada — ninguém consegue ver.',
  },
};

/**
 * A regra dos quatro estados, num lugar só.
 *
 * Antes ela morava num helper dentro do JSX da tela legada, o que tornava impossível testá-la —
 * e é a regra mais importante desta tela.
 */
export function statusDaPagina(p) {
  if (p.isPublished) return p.hasPendingChanges ? 'pending' : 'published';
  // Já esteve no ar e saiu: tem data de publicação mas não está publicada.
  if (p.publishedAt) return 'unpublished';
  return 'draft';
}

/** Ordem de URGÊNCIA, não alfabética: primeiro o que pede ação. */
const PESO = { unpublished: 0, pending: 1, draft: 2, published: 3 };

const dataOuNull = (iso, prefixo) => (iso ? `${prefixo} ${formatDate(iso)}` : null);

/** Estado do domínio próprio, no vocabulário desta tela. */
function montarDominio(d) {
  if (!d) return null;

  const noAr = d.status === 'active';
  const falhou = d.status === 'failed' || d.status === 'expired';

  return {
    domain: d.domain,
    url: `https://${d.domain}`,
    statusLabel: noAr ? 'No ar' : falhou ? 'Com problema' : 'Configurando',
    statusTone: noAr ? 'success' : falhou ? 'error' : 'warning',
    isLive: noAr,
  };
}

function montarPagina(p, dominioPorPagina) {
  const status = statusDaPagina(p);
  const noAr = status === 'published' || status === 'pending';

  return {
    id: p.id,
    title: p.title,
    slug: p.slug,
    publicUrl: `${URL_BASE}/${p.slug}`,
    status,
    ...ESTADO[status],
    type: p.type,
    typeLabel: TIPO[p.type] || 'Página',
    templateName: p.templateName || null,
    publishedAtLabel: p.isPublished
      ? dataOuNull(p.publishedAt, 'no ar desde')
      : dataOuNull(p.publishedAt, 'saiu do ar em'),
    updatedAtLabel: p.draftUpdatedAt ? `editada ${formatRelativeDistance(p.draftUpdatedAt)}` : null,
    createdAtLabel: `criada em ${formatDate(p.createdAt)}`,
    customDomain: montarDominio(dominioPorPagina.get(p.id)),
    canPublish: !p.isPublished || p.hasPendingChanges,
    canUnpublish: noAr,
    // Regra do servidor: não se exclui o que está no ar. O motivo vai junto para o botão não
    // ficar mudo — negar sem dizer por quê é o pior dos mundos.
    canDelete: !noAr,
    blockedReason: noAr ? 'Despublique antes de excluir.' : null,
  };
}

function montarResumo(paginas) {
  const conta = (s) => paginas.filter((p) => p.status === s).length;
  const foraDoAr = conta('unpublished');
  const desatualizadas = conta('pending');

  const plural = (n, um, muitos) => `${n} ${n === 1 ? um : muitos}`;

  const headline = (() => {
    if (paginas.length === 0) return null;
    if (foraDoAr && desatualizadas) {
      return `${plural(foraDoAr, 'página fora do ar', 'páginas fora do ar')} e `
           + `${plural(desatualizadas, 'com alterações não publicadas', 'com alterações não publicadas')}.`;
    }
    if (foraDoAr) return `${plural(foraDoAr, 'página fora do ar', 'páginas fora do ar')}.`;
    if (desatualizadas) {
      return `${plural(desatualizadas, 'página', 'páginas')} com alterações não publicadas.`;
    }
    if (conta('published') > 0) return 'Tudo no ar e atualizado.';
    return null;
  })();

  return {
    total: paginas.length,
    published: conta('published'),
    pending: desatualizadas,
    unpublished: foraDoAr,
    drafts: conta('draft'),
    headline,
    headlineTone: foraDoAr ? 'error' : desatualizadas ? 'warning' : headline ? 'success' : 'neutral',
  };
}

/**
 * A oferta de domínio DESTA tela.
 *
 * ⚠️ Quem decide se ela pode aparecer é o servidor (`state === 'offer'`, que já embute "tem
 * página publicada e não tem domínio"). Aqui não se reimplementa essa regra — só se traduz.
 */
function montarOferta(status, paginas) {
  const vazia = {
    visible: false,
    title: '', pitch: '', suggestions: [], priceLabel: '', periodLabel: '', ctaLabel: '', pageTitle: '',
  };

  if (!status || status.state !== 'offer') return vazia;

  const alvo = paginas.find((p) => p.status === 'published') || paginas[0];
  if (!alvo) return vazia;

  // `formatCurrency` e não `toLocaleString` na mão: é o formatador de dinheiro do projeto, e o
  // guard de timezone barra formatação solta (a regra é abrangente de propósito).
  const priceLabel = formatCurrency((status.priceCents ?? 0) / 100);

  return {
    visible: true,
    title: 'Seu endereço, no seu nome',
    pitch: `Em vez de ${alvo.publicUrl.replace(/^https?:\/\//, '')}, o link que você entrega vira `
         + 'um endereço curto e seu. Mesma página, endereço próprio.',
    suggestions: (status.suggestions ?? []).slice(0, 3).map((s) => s.domain ?? s),
    priceLabel,
    periodLabel: 'por ano',
    ctaLabel: 'Ver nomes livres',
    pageTitle: alvo.title,
  };
}

const VAZIO = {
  title: 'Você ainda não tem endereço na internet',
  message: 'Uma página do Agendário é o link que você manda no WhatsApp, cola no Instagram e '
         + 'imprime no cartão. Quem abre já marca horário por ali.',
  reasons: [
    'Seus clientes agendam sozinhos, a qualquer hora',
    'Fica pronta hoje, sem contratar ninguém',
    'Some do ar quando você quiser, com um clique',
  ],
  ctaLabel: 'Criar minha primeira página',
};

const TIPOS_OFERECIDOS = [
  {
    type: 1,
    label: 'Link in Bio',
    description: 'Uma página curta com seus links, para colar na bio da rede social.',
    highlights: ['Pronta em minutos', 'Links para redes e WhatsApp', 'Botão de agendar'],
    recommended: false,
    badge: null,
    enabled: true,
  },
  {
    type: 2,
    label: 'Profissional',
    description: 'Sua página pessoal: experiência, especialidades e agenda de atendimentos.',
    highlights: ['Sua foto e sua história', 'Especialidades', 'Agenda pública'],
    recommended: false,
    badge: null,
    enabled: true,
  },
  {
    type: 3,
    label: 'Empresa',
    description: 'O site do seu negócio: equipe, serviços, endereço e agendamento.',
    highlights: ['Equipe completa', 'Catálogo de serviços', 'Mapa e horários', 'Agendamento'],
    recommended: true,
    badge: null,
    enabled: true,
  },
];

/** @returns {import('./pages.vm.js').PagesVm} */
export function montarVm({
  pages,
  domainStatus,
  carregando,
  errorMessage,
  refreshing = false,
  dialogos = {},
  feedback = null,
}) {
  const state = (() => {
    if (carregando && pages === undefined) return 'loading';
    if (errorMessage && pages === undefined) return 'error';
    if (!pages) return 'error';
    return pages.length === 0 ? 'empty' : 'ready';
  })();

  // Qual página responde em qual domínio. É o dado que a tela antiga ignorava por completo, e a
  // única coisa aqui pela qual o cliente pagou.
  const dominioPorPagina = new Map();
  for (const d of domainStatus?.domains ?? []) {
    if (d.rootPage?.id) dominioPorPagina.set(d.rootPage.id, d);
  }

  const lista = (pages ?? [])
    .map((p) => montarPagina(p, dominioPorPagina))
    .sort((a, b) => (PESO[a.status] - PESO[b.status]) || a.title.localeCompare(b.title, 'pt-BR'));

  return {
    state,
    errorMessage: errorMessage ?? null,
    refreshing,
    title: 'Páginas e links',
    subtitle: 'Os endereços que você entrega para os seus clientes.',
    createLabel: 'Nova página',
    summary: montarResumo(lista),
    pages: lista,
    domainOffer: montarOferta(domainStatus, lista),
    empty: VAZIO,
    typeDialog: {
      open: !!dialogos.type,
      title: 'Que tipo de página você quer criar?',
      subtitle: 'Dá para mudar o visual depois, mas o tipo define o que a página sabe fazer.',
      options: TIPOS_OFERECIDOS,
    },
    publishDialog: montarPublicacao(dialogos.publish, lista),
    unpublishDialog: montarDespublicacao(dialogos.unpublish, lista),
    deleteDialog: montarExclusao(dialogos.delete, lista),
    previewDialog: montarPreview(dialogos.preview, lista),
    feedback,
  };
}

const achar = (lista, id) => lista.find((p) => p.id === id) || null;

function montarPublicacao(d, lista) {
  const alvo = d ? achar(lista, d.id) : null;
  const republicando = alvo?.status === 'pending';

  return {
    open: !!d,
    pageTitle: alvo?.title ?? '',
    // O texto MUDA: colocar no ar pela primeira vez e substituir o que já está lá são coisas
    // diferentes, e a segunda merece o aviso.
    title: republicando ? 'Publicar as alterações?' : 'Publicar esta página?',
    message: republicando
      ? 'A versão que está no ar será substituída pelo que você editou.'
      : 'Ela fica visível para qualquer pessoa com o link.',
    confirmLabel: republicando ? 'Publicar alterações' : 'Publicar',
    saving: !!d?.saving,
    error: d?.error ?? null,
  };
}

function montarDespublicacao(d, lista) {
  const alvo = d ? achar(lista, d.id) : null;

  return {
    open: !!d,
    pageTitle: alvo?.title ?? '',
    title: 'Tirar esta página do ar?',
    message: 'Ela deixa de ser visível imediatamente.',
    warning: 'Quem já tem o link salvo passa a ver uma página que não abre.',
    confirmLabel: 'Tirar do ar',
    saving: !!d?.saving,
    error: d?.error ?? null,
  };
}

function montarExclusao(d, lista) {
  const alvo = d ? achar(lista, d.id) : null;
  const bloqueado = !!alvo && !alvo.canDelete;

  return {
    open: !!d,
    pageTitle: alvo?.title ?? '',
    title: bloqueado ? 'Esta página está no ar' : 'Excluir esta página?',
    message: bloqueado
      ? 'Não dá para excluir uma página publicada. Tire do ar primeiro e depois exclua.'
      : 'Esta ação não pode ser desfeita.',
    blocked: bloqueado,
    blockedReason: bloqueado ? alvo.blockedReason : null,
    // Negar sem oferecer caminho seria beco sem saída.
    unblockLabel: bloqueado ? 'Tirar do ar agora' : null,
    confirmLabel: 'Excluir',
    saving: !!d?.saving,
    error: d?.error ?? null,
  };
}

function montarPreview(d, lista) {
  const alvo = d ? achar(lista, d.id) : null;

  return {
    open: !!d,
    pageTitle: alvo?.title ?? '',
    // Preferir o domínio próprio: é o endereço que o cliente de fato entrega.
    url: alvo?.customDomain?.isLive ? alvo.customDomain.url : (alvo?.publicUrl ?? ''),
    closeLabel: 'Fechar',
  };
}
